import { FC, MouseEvent, useState } from 'react';
import styled, { css } from 'styled-components';

import Button from '@/components/Button';
import Checkbox from '@/components/Checkbox';
import FancySwitch from '@/components/FancySwitch';
import { Input, RadioCircle, RadioInput, Textarea } from '@/components/Forms';
import ShowHideButton from '@/components/ShowHideButton';
import Text from '@/components/Text';
import { getOptimalFontSize } from '@/utils/coverImages/textDistribution';

import SimpleUploadedImageRow from '../SimpleUploadedImageRow';
import UploadSingleImage from '../UploadSingleImage';
import {
  Box,
  ButtonHolder,
  Header,
  HeaderButtons,
  Heading,
  ImageData,
  LabelText,
  ResetButton,
  UploadedImages,
} from '../VariationOne';
import {
  ImageGrid,
  StyledTextArea,
  TextAreaBox,
  TextParametersBox,
} from '../VariationTwo';
import Filler from '../shared/Filler';
import {
  ActionBox,
  ActionTitle,
  StyledRadiosLabel,
} from '../shared/styledComponents';
import GeneratedImages from './GeneratedImages';
import VariationsStyles from './VariationsStyles';

export const CHANNEL_NAME_COLORS = ['black', 'blue'];

export const CHANNEL_IMAGE_OPTIONS = ['custom', 'reddit'];

export interface ImageVariation {
  leftText: string;
  rightText: string;
  authorImage?: string;
  authorImageOriginal?: ImageData[];
  authorName: string;
  divider: string;
  channelNameColor: string;
  leftBrakes: boolean;
  brakes: boolean;
  leftFontSize?: number;
  fontSize?: number;
  channelImageOption: string;
  reversed?: boolean;
}

export const InputsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
`;

export const GridInner = styled.div<{ $noBottomPadding?: boolean }>`
  padding: 24px;
  border-top: 1px solid ${({ theme }) => theme.colors.grayscaleLight};
  &:nth-child(even) {
    border-left: 1px solid ${({ theme }) => theme.colors.grayscaleLight};
  }
  ${({ $noBottomPadding }) =>
    $noBottomPadding &&
    css`
      padding-bottom: 0;
    `}
`;

export const NameTextArea = styled(Textarea)`
  height: 98px;
`;

const UploadAuthorImages = styled(UploadSingleImage)`
  height: 98px;
`;

const ChannelImageHolder = styled.div`
  margin-top: 16px;
`;

const ChannelNameHolder = styled.div`
  margin-top: 16px;
`;

const HeadingsHolder = styled.div`
  flex: 1;
`;

getOptimalFontSize('some random text', {
  maxWidth: 530,
  maxHeight: 560,
  fontFamily: 'Gothic',
});

interface Props {
  inArticle?: boolean;
}

const VariationBoldComment: FC<Props> = ({ inArticle }) => {
  const [showContent, setShowContent] = useState(true);
  const [brakes, setBrakes] = useState(false);
  const [leftBrakes, setLeftBrakes] = useState(false);
  const [leftText, setLeftText] = useState('');
  const [leftFontSize, setLeftFontSize] = useState('');
  const [text, setText] = useState('');
  const [fontSize, setFontSize] = useState('');
  const [isReversed, setIsReversed] = useState(false);

  const [variations, setVariations] = useState<ImageVariation[]>([]);
  const [authorImages, setAuthorImages] = useState<ImageData[]>([]);
  const [channelImageOption, setChannelImageOption] = useState('custom');

  const [authorName, setAuthorName] = useState('');
  const [nameColor, setNameColor] = useState('black');
  const [images, setImages] = useState<string[]>();

  const onReset = () => {
    setVariations([]);
    setImages([]);
    setAuthorImages([]);
    setBrakes(false);
    setLeftBrakes(false);
    setLeftText('');
    setLeftFontSize('');
    setText('');
    setAuthorName('');
    setIsReversed(false);
    setFontSize('');
    setNameColor('black');
    setChannelImageOption('custom');
  };

  const generateVariations = async (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    const newVariations = () => [
      {
        rightText: text,
        leftText: leftText,
        brakes: brakes,
        leftBrakes: leftBrakes,
        divider: 'black',
        fontSize: fontSize ? Number(fontSize) : undefined,
        leftFontSize: leftFontSize ? Number(leftFontSize) : undefined,
        authorName: authorName,
        authorImage: !!authorImages.length
          ? authorImages[0]?.croppedImage ??
            URL.createObjectURL(authorImages[0].image)
          : undefined,
        channelNameColor: nameColor,
        channelImageOption: channelImageOption,
        reversed: isReversed,
      },
    ];

    setVariations(newVariations);
  };

  const renderAuthorImages = authorImages.map((image, index) => (
        <SimpleUploadedImageRow
          key={`${index}-${image.image.name}-${image.image.lastModified}`}
          imageIndex={index}
          image={image}
          setImages={setAuthorImages}
        />
      ));

  const isResetDisabled =
    !fontSize &&
    !text &&
    !brakes &&
    !authorImages.length &&
    !authorName &&
    nameColor === 'black' &&
    channelImageOption === 'custom' &&
    !leftText &&
    !leftFontSize &&
    !leftBrakes &&
    !isReversed;

  return (
    <>
      <Box>
        <Header>
          <HeadingsHolder>
            <Heading $type="button">Bold Text + Comment</Heading>
            <Text $type="smallHeadline" $color="gray">
              Attach Bold text on the left side and add text on the right with
              the necessary parameters. Additionally, include the author's image
              and name
            </Text>
          </HeadingsHolder>
          <HeaderButtons>
            <FancySwitch
              isActive={isReversed}
              onClick={() => setIsReversed((prev) => !prev)}
              text="Reversed"
            />
            <ResetButton
              color="red"
              disabled={isResetDisabled}
              onClick={onReset}
            >
              Reset
            </ResetButton>
            <ShowHideButton
              showContent={showContent}
              onClick={() => setShowContent((prev) => !prev)}
            />
          </HeaderButtons>
        </Header>
        {showContent && (
          <>
            <ImageGrid>
              <TextParametersBox $reversed={!isReversed}>
                <TextAreaBox $reversed={!isReversed}>
                  <div>
                    <LabelText $type="smallHeadline">
                      {isReversed ? 'Right' : 'Left'} Text
                    </LabelText>
                    <StyledTextArea
                      onChange={(e) => setLeftText(e.target.value)}
                      value={leftText}
                      placeholder="Add text"
                    ></StyledTextArea>
                  </div>
                  <div>
                    <LabelText $type="smallHeadline">Font Size</LabelText>
                    <Input
                      value={leftFontSize}
                      onChange={(e) => setLeftFontSize(e.target.value)}
                      placeholder="Font Size (px)"
                      type="number"
                      min="0"
                    />
                  </div>
                  <div>
                    <ActionTitle $type="smallHeadline">
                      Text Parameters
                    </ActionTitle>
                    <ActionBox>
                      <Checkbox
                        checked={leftBrakes}
                        onChange={() => setLeftBrakes((prev) => !prev)}
                      >
                        Custom brakes
                      </Checkbox>
                    </ActionBox>
                  </div>
                </TextAreaBox>
              </TextParametersBox>
              <TextParametersBox $reversed={isReversed}>
                <TextAreaBox $reversed={isReversed}>
                  <div>
                    <LabelText $type="smallHeadline">
                      {isReversed ? 'Left' : 'Right'} Text
                    </LabelText>
                    <StyledTextArea
                      onChange={(e) => setText(e.target.value)}
                      value={text}
                      placeholder="Add text"
                    ></StyledTextArea>
                  </div>
                  <div>
                    <LabelText $type="smallHeadline">Font Size</LabelText>
                    <Input
                      value={fontSize}
                      onChange={(e) => setFontSize(e.target.value)}
                      placeholder="Font Size (px)"
                      type="number"
                      min="0"
                    />
                  </div>
                  <div>
                    <ActionTitle $type="smallHeadline">
                      Text Parameters
                    </ActionTitle>
                    <ActionBox>
                      <Checkbox
                        checked={brakes}
                        onChange={() => setBrakes((prev) => !prev)}
                      >
                        Custom brakes
                      </Checkbox>
                    </ActionBox>
                  </div>
                </TextAreaBox>
              </TextParametersBox>
            </ImageGrid>
            <InputsGrid>
              <GridInner $noBottomPadding={!!authorImages?.length}>
                <LabelText $type="smallHeadline">Channel Image</LabelText>
                <ActionBox>
                  {CHANNEL_IMAGE_OPTIONS.map((option) => (
                    <StyledRadiosLabel key={option}>
                      {option}
                      <RadioInput
                        type="radio"
                        name="channel_image"
                        checked={channelImageOption === option}
                        onChange={() => setChannelImageOption(option)}
                      />
                      <RadioCircle />
                    </StyledRadiosLabel>
                  ))}
                </ActionBox>
                {channelImageOption === 'custom' && (
                  <ChannelImageHolder>
                    <LabelText $type="smallHeadline">
                      Add Channel Image
                    </LabelText>
                    {!!authorImages?.length ? (
                      <UploadedImages>{renderAuthorImages}</UploadedImages>
                    ) : (
                      <UploadAuthorImages
                        coverIndex={2}
                        setImages={setAuthorImages}
                      />
                    )}
                  </ChannelImageHolder>
                )}
              </GridInner>
              <GridInner>
                <LabelText $type="smallHeadline">Channel Name Color</LabelText>
                <ActionBox>
                  {CHANNEL_NAME_COLORS.map((color) => (
                    <StyledRadiosLabel key={color}>
                      {color}
                      <RadioInput
                        type="radio"
                        name="name_color"
                        checked={nameColor === color}
                        onChange={() => setNameColor(color)}
                      />
                      <RadioCircle />
                    </StyledRadiosLabel>
                  ))}
                </ActionBox>
                <ChannelNameHolder>
                  <LabelText $type="smallHeadline">Channel Name</LabelText>
                  <NameTextArea
                    placeholder="Add Name"
                    onChange={(e) => setAuthorName(e.target.value)}
                    value={authorName}
                  ></NameTextArea>
                </ChannelNameHolder>
              </GridInner>
            </InputsGrid>
          </>
        )}
      </Box>
      <Filler fontFamily="Gothic" />
      <VariationsStyles />
      {showContent && (
        <ButtonHolder>
          <Button onClick={generateVariations}>
            {!!variations?.length ? 'Regenerate' : 'Generate'}
          </Button>
        </ButtonHolder>
      )}
      <GeneratedImages
        variations={variations}
        images={images}
        setImages={setImages}
        inArticle={inArticle}
        onReset={onReset}
      />
    </>
  );
};

export default VariationBoldComment;
